import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircleIcon, CheckCircle2Icon, CheckIcon, ChevronDownIcon, CircleXIcon, ClockIcon, FrownIcon, GiftIcon, HelpCircleIcon, QuoteIcon, RepeatIcon, ShieldIcon, SparklesIcon, TrophyIcon, XIcon } from 'lucide-react';
import { bonuses, faqs, moreTestimonials, testimonials, tomos } from '../data/content';
import { Button } from './ui/Button';
function Eyebrow({
  children,
  danger = false
}: {
  children: React.ReactNode;
  danger?: boolean;
}) {
  return <p className={`mb-3 text-center text-[11px] font-bold uppercase tracking-[.16em] md:text-sm ${danger ? 'text-red-500' : 'text-teal'}`} data-id="element-18">
      {children}
    </p>;
}
function SectionTitle({
  children
}: {
  children: React.ReactNode;
}) {
  return <h2 className="mx-auto max-w-3xl text-center text-2xl font-extrabold leading-tight text-slate-900 md:text-4xl" data-id="element-19">
      {children}
    </h2>;
}
type Testimonial = {
  initials: string;
  name: string;
  place: string;
  text: string;
};
function TestimonialCard({
  item
}: {
  item: Testimonial;
}) {
  return <article className="relative flex flex-col rounded-2xl border border-sky-100 bg-white p-6 shadow-sm" data-id="element-20">
      <QuoteIcon className="absolute right-5 top-5 h-7 w-7 text-teal/15" data-id="element-21" />
      <div className="mb-4 text-sm text-gold" data-id="element-22">★★★★★</div>
      <p className="mb-5 flex-1 text-base font-medium italic leading-relaxed text-slate-700" data-id="element-23">
        “{item.text}”
      </p>
      <div className="flex items-center gap-3 border-t border-slate-100 pt-4" data-id="element-24">
        <span className="flex h-11 w-11 items-center justify-center rounded-full border-2 border-teal/20 bg-teal/10 font-display text-sm font-bold text-teal" data-id="element-25">
          {item.initials}
        </span>
        <div data-id="element-26">
          <p className="text-sm font-bold text-slate-900" data-id="element-27">{item.name}</p>
          <p className="text-xs text-slate-500" data-id="element-28">{item.place}</p>
        </div>
      </div>
    </article>;
}
export function Testimonials() {
  return <section className="mx-auto max-w-6xl px-4 py-16 md:py-20" data-id="element-29">
      <Eyebrow data-id="element-30">⭐ Lo que dicen los que ya lo tienen</Eyebrow>
      <SectionTitle data-id="element-31">
        Entrenadores que ya trabajan con{' '}
        <span className="text-teal" data-id="element-32">un sistema real</span>
      </SectionTitle>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-id="element-33">
        {testimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-34" />)}
      </div>
    </section>;
}
export function PainAndTransformation() {
  const pains = ['Repetís siempre los mismos ejercicios porque no tenés una progresión planificada', 'No sabés qué hacer cuando un pibe llega con una molestia o lesión', 'Gym y cancha van por carriles separados y no se conectan', 'Cada sesión queda sin un hilo conductor claro', 'Perdés jugadores porque no ven progreso concreto', 'Tardás horas armando entrenamientos que podrían estar listos en 5 minutos'];
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-16 md:py-24" data-id="element-35">
        <div className="mx-auto max-w-4xl" data-id="element-36">
          <Eyebrow danger data-id="element-37">😤 La realidad</Eyebrow>
          <SectionTitle data-id="element-38">
            ¿Cuántas veces más vas a improvisar{' '}
            <span className="text-teal" data-id="element-39">una sesión?</span>
          </SectionTitle>
          <p className="mx-auto mt-6 max-w-3xl text-center font-medium text-slate-600" data-id="element-40">
            Amás lo que hacés. Pero esto te pasa seguido:
          </p>
          <div className="mt-9 grid gap-3 sm:grid-cols-2 md:gap-4" data-id="element-41">
            {pains.map(pain => <div key={pain} className="flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/60 p-4" data-id="element-42">
                <CircleXIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-500" data-id="element-43" />
                <span className="text-sm font-medium text-slate-700 md:text-base" data-id="element-44">
                  {pain}
                </span>
              </div>)}
          </div>
          <div className="mt-10 rounded-2xl border border-teal/20 bg-sky-50 p-6 text-center" data-id="element-45">
            <p className="text-lg font-bold text-slate-700 md:text-2xl" data-id="element-46">
              No es falta de ganas.
              <br data-id="element-47" />
              <span className="text-xl text-teal md:text-3xl" data-id="element-48">
                Es falta de sistema.
              </span>
            </p>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:py-24" data-id="element-49">
        <Eyebrow data-id="element-50">
          <span className="inline-flex items-center gap-2" data-id="element-51">
            <SparklesIcon className="h-4 w-4" data-id="element-52" /> Con el Combo Completo Futsal
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-53">
          Tu próxima sesión puede ser{' '}
          <span className="text-teal" data-id="element-54">completamente diferente.</span>
        </SectionTitle>
        <div className="mt-8 rounded-3xl border border-teal/20 bg-sky-50 p-6 shadow-[0_10px_40px_rgba(7,129,139,.10)] md:p-10" data-id="element-55">
          <p className="text-center text-base font-bold leading-relaxed text-slate-900 md:text-xl" data-id="element-56">
            Abrís el tomo. Elegís los ejercicios. Entrenás.
          </p>
          <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5" data-id="element-57">
            {['200+ ejercicios organizados.', 'Protocolo ante lesiones.', 'Gym y cancha integrados.', 'Progresión real por nivel.', 'Jugadores que avanzan.'].map(item => <div key={item} className="rounded-xl border border-teal/20 bg-white/80 p-3 text-center text-xs font-bold text-teal md:text-sm" data-id="element-58">
                {item}
              </div>)}
          </div>
          <p className="mt-6 text-center font-medium text-slate-600" data-id="element-59">
            Eso es el{' '}
            <strong className="text-slate-900" data-id="element-60">Combo Completo Futsal.</strong>
          </p>
        </div>
        <div className="mt-8 text-center" data-id="element-61">
          <Button href="#pricing" data-id="element-62">QUIERO EL COMBO COMPLETO FUTSAL</Button>
        </div>
      </section>
    </>;
}
export function ProductOverview() {
  const [open, setOpen] = useState(0);
  const values = ['+200 ejercicios organizados', '5 Tomos completos', 'Cancha + gym en un sistema', 'Prevención de lesiones integrada', 'Creado para entrenadores', 'Adaptado para Argentina'];
  return <section className="mx-auto max-w-5xl px-4 py-16 md:py-24" data-id="element-63">
      <Eyebrow data-id="element-64">
        <span className="inline-flex items-center gap-2" data-id="element-65">
          <TrophyIcon className="h-4 w-4" data-id="element-66" /> La solución
        </span>
      </Eyebrow>
      <SectionTitle data-id="element-67">
        El sistema completo para resolver tus{' '}
        <span className="text-teal" data-id="element-68">entrenamientos de Futsal.</span>
      </SectionTitle>
      <p className="mt-4 text-center font-medium text-slate-600" data-id="element-69">
        5 Tomos + 5 Bonos de Gimnasio · Cancha, fuerza y planificación · Todo
        resuelto
      </p>

      <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-3" data-id="element-70">
        {values.map(value => <div key={value} className="flex items-center gap-3 rounded-xl border border-sky-100 bg-white p-4 shadow-sm" data-id="element-71">
            <span className="rounded-lg bg-teal/10 p-2" data-id="element-72">
              <CheckIcon className="h-4 w-4 text-teal" data-id="element-73" />
            </span>
            <span className="text-sm font-bold leading-tight text-slate-800" data-id="element-74">
              {value}
            </span>
          </div>)}
      </div>

      <div className="mt-16" data-id="element-75">
        <h3 className="text-center text-2xl font-extrabold text-slate-900 md:text-4xl" data-id="element-76">
          Los 5 Tomos del Sistema.{' '}
          <span className="text-teal" data-id="element-77">Todo lo que tu equipo necesita.</span>
        </h3>
        <p className="mt-3 text-center text-sm font-medium text-slate-500" data-id="element-78">
          Tocá un tomo para ver el contenido
        </p>
        <div className="mt-8 space-y-3" data-id="element-79">
          {tomos.map((tomo, index) => <div className={`overflow-hidden rounded-2xl border bg-white transition-colors ${open === index ? 'border-teal shadow-lg shadow-teal/10' : 'border-sky-100'}`} key={tomo.name} data-id="element-80">
              <button className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left md:px-7 md:py-5" aria-expanded={open === index} onClick={() => setOpen(open === index ? -1 : index)} data-id="element-81">
                <span className="flex items-center gap-3" data-id="element-82">
                  <span className="text-2xl" data-id="element-83">{tomo.icon}</span>
                  <span data-id="element-84">
                    <b className="block text-slate-900" data-id="element-85">
                      Tomo {index + 1} — {tomo.name}
                    </b>
                    <small className="font-bold text-teal" data-id="element-86">{tomo.period}</small>
                  </span>
                </span>
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-teal transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-87" />
              </button>
              <AnimatePresence data-id="element-88">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} data-id="element-89">
                    <ul className="space-y-2 border-t border-sky-100 px-5 py-4 text-sm font-medium text-slate-600 md:px-7" data-id="element-90">
                      {tomo.content.map(line => <li key={line} className="flex gap-2" data-id="element-91">
                          <span className="text-teal" data-id="element-92">→</span>
                          {line}
                        </li>)}
                    </ul>
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </div>

      <div className="mt-16 rounded-2xl border border-sky-100 bg-white p-6 shadow-sm md:p-8" data-id="element-93">
        <h3 className="text-center text-xl font-extrabold text-slate-900 md:text-2xl" data-id="element-94">
          Cada tomo viene listo para{' '}
          <span className="text-teal" data-id="element-95">entrar a la cancha y usar</span>
        </h3>
        <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-id="element-96">
          {['Planificación semanal lista para aplicar', 'Ejercicios con consignas claras', 'Prevención de lesiones integrada', 'Progresiones por objetivo y nivel', 'Fichas para seguir el progreso'].map(item => <p key={item} className="flex gap-2 text-sm font-medium text-slate-700" data-id="element-97">
              <CheckCircle2Icon className="h-5 w-5 shrink-0 text-teal" data-id="element-98" />
              {item}
            </p>)}
        </div>
      </div>
    </section>;
}
export function BeforeAfterAndBonuses() {
  const rows = [['Improvisás cada sesión', 'Abrís y ejecutás'], ['Siempre los mismos ejercicios', '200+ ejercicios organizados'], ['Sin respaldo ante lesiones', 'Protocolo exacto disponible'], ['Gym y cancha separados', 'Sistema integrado completo'], ['Sin progresión real', 'Planificación clara por objetivo'], ['Jugadores que abandonan', 'Jugadores que progresan'], ['2 o 3 horas armando sesiones', '5 minutos y listo']];
  return <>
      <section className="border-y border-sky-100 bg-white px-4 py-16 md:py-24" data-id="element-99">
        <div className="mx-auto max-w-5xl" data-id="element-100">
          <Eyebrow data-id="element-101">🔄 Antes vs. después</Eyebrow>
          <SectionTitle data-id="element-102">
            No comprás solo archivos PDF.{' '}
            <span className="text-teal" data-id="element-103">Comprás un sistema completo.</span>
          </SectionTitle>
          <div className="mt-10 grid gap-4 md:grid-cols-2 md:gap-6" data-id="element-104">
            <div className="rounded-2xl border border-red-200 bg-red-50/40 p-6" data-id="element-105">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-106">
                <XIcon className="rounded-lg bg-red-100 p-2 text-red-500" size={40} data-id="element-107" />
                Antes — Sin sistema
              </h3>
              {rows.map(([before]) => <p key={before} className="mb-3 flex gap-2 text-sm font-medium text-slate-600" data-id="element-108">
                  <XIcon className="h-4 w-4 shrink-0 text-red-500" data-id="element-109" />
                  {before}
                </p>)}
            </div>
            <div className="rounded-2xl border-2 border-teal bg-white p-6 shadow-lg shadow-teal/10" data-id="element-110">
              <h3 className="mb-5 flex items-center gap-2 font-bold text-slate-900" data-id="element-111">
                <CheckIcon className="rounded-lg bg-teal/10 p-2 text-teal" size={40} data-id="element-112" />
                Después — Con el Combo Completo
              </h3>
              {rows.map(([, after]) => <p key={after} className="mb-3 flex gap-2 text-sm font-bold text-slate-800" data-id="element-113">
                  <CheckIcon className="h-4 w-4 shrink-0 text-teal" data-id="element-114" />
                  {after}
                </p>)}
            </div>
          </div>
          <p className="mt-10 text-center text-lg font-bold text-slate-700 md:text-2xl" data-id="element-115">
            Recuperás <span className="text-teal" data-id="element-116">tiempo, claridad mental</span>{' '}
            y <span className="text-teal" data-id="element-117">seguridad como entrenador.</span>
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 py-16 md:py-24" data-id="element-118">
        <Eyebrow data-id="element-119">
          <span className="inline-flex items-center gap-2" data-id="element-120">
            <GiftIcon className="h-4 w-4" data-id="element-121" /> Bonos de Gimnasio
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-122">
          Además te llevás{' '}
          <span className="text-teal" data-id="element-123">
            5 bonos de gimnasio incluidos gratis
          </span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-id="element-124">
          {bonuses.map(([number, icon, name, description, value]) => <article key={number} className="flex flex-col rounded-2xl border border-sky-100 bg-white p-5 shadow-sm" data-id="element-125">
              <div className="flex gap-3" data-id="element-126">
                <span className="text-3xl" data-id="element-127">{icon}</span>
                <div data-id="element-128">
                  <small className="font-bold uppercase tracking-wide text-teal" data-id="element-129">
                    Bono {number}
                  </small>
                  <h3 className="font-bold text-slate-900" data-id="element-130">{name}</h3>
                </div>
              </div>
              <p className="my-4 flex-1 text-sm font-medium leading-relaxed text-slate-600" data-id="element-131">
                {description}
              </p>
              <div className="flex border-t border-slate-100 pt-4 text-sm font-bold" data-id="element-132">
                <span className="text-slate-400 line-through" data-id="element-133">
                  Valor: {value}
                </span>
                <span className="ml-auto text-teal" data-id="element-134">GRATIS</span>
              </div>
            </article>)}
        </div>
        <div className="mx-auto mt-10 max-w-2xl rounded-2xl border border-teal/20 bg-teal/5 p-6 text-center" data-id="element-135">
          <p className="font-medium text-slate-600" data-id="element-136">
            Valor total de bonos:{' '}
            <s className="font-bold text-slate-400" data-id="element-137">$60.000</s>
          </p>
          <p className="mt-1 text-lg font-extrabold text-slate-900" data-id="element-138">
            Hoy van <span className="text-teal" data-id="element-139">incluidos gratis</span> con tu
            compra.
          </p>
        </div>
      </section>
    </>;
}
export function SocialAndFAQ() {
  const [open, setOpen] = useState(0);
  return <>
      <section className="mx-auto max-w-6xl px-4 py-16 md:py-24" data-id="element-140">
        <Eyebrow data-id="element-141">🇦🇷 Más testimonios reales</Eyebrow>
        <SectionTitle data-id="element-142">
          Lo que cambia cuando entrenás con{' '}
          <span className="text-teal" data-id="element-143">un método</span>
        </SectionTitle>
        <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3 md:gap-6" data-id="element-144">
          {moreTestimonials.map(item => <TestimonialCard key={item.initials} item={item} data-id="element-145" />)}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-4 py-16 md:py-24" data-id="element-146">
        <Eyebrow data-id="element-147">
          <span className="inline-flex items-center gap-2" data-id="element-148">
            <HelpCircleIcon className="h-4 w-4" data-id="element-149" /> Preguntas frecuentes
          </span>
        </Eyebrow>
        <SectionTitle data-id="element-150">
          Lo que más <span className="text-teal" data-id="element-151">nos preguntan</span>
        </SectionTitle>
        <div className="mt-10 space-y-3" data-id="element-152">
          {faqs.map(([question, answer], index) => <div key={question} className={`overflow-hidden rounded-2xl border bg-white ${open === index ? 'border-teal/50 shadow-lg shadow-teal/10' : 'border-sky-100'}`} data-id="element-153">
              <button className="flex w-full items-center justify-between gap-3 p-5 text-left font-bold text-slate-900 md:px-6" onClick={() => setOpen(open === index ? -1 : index)} aria-expanded={open === index} data-id="element-154">
                {question}
                <ChevronDownIcon className={`h-5 w-5 shrink-0 text-teal transition-transform ${open === index ? 'rotate-180' : ''}`} data-id="element-155" />
              </button>
              <AnimatePresence data-id="element-156">
                {open === index && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} className="border-t border-sky-100 px-5 py-4 text-sm font-medium leading-relaxed text-slate-600 md:px-6" data-id="element-157">
                    {answer}
                  </motion.div>}
              </AnimatePresence>
            </div>)}
        </div>
      </section>
    </>;
}
export function GuaranteeAndCost() {
  const costs = [[ClockIcon, '3 horas de planificación × 52 semanas', '156 horas de tu vida por año'], [AlertCircleIcon, 'Sin protocolos de lesiones', 'Riesgo profesional ante cada consulta'], [FrownIcon, 'Sin fichas de evaluación', 'Jugadores que no ven progreso y se van'], [RepeatIcon, 'Sin periodización real', 'Equipos que bajan el ritmo en los torneos']];
  return <>
      <section className="border-y border-sky-100 bg-sky-50/70 px-4 py-16 md:py-20" data-id="element-158">
        <div className="mx-auto max-w-3xl text-center" data-id="element-159">
          <span className="inline-flex rounded-full border-2 border-teal bg-teal/10 p-5 shadow-lg shadow-teal/10" data-id="element-160">
            <ShieldIcon className="h-10 w-10 text-teal" data-id="element-161" />
          </span>
          <Eyebrow data-id="element-162">🛡️ Sin riesgo · 100% garantía</Eyebrow>
          <SectionTitle data-id="element-163">
            Probalo 7 días. Si no te sirve,{' '}
            <span className="text-teal" data-id="element-164">te devolvemos el 100%.</span>
          </SectionTitle>
          <div className="mt-7 rounded-2xl border border-sky-100 bg-white p-6 text-left font-medium leading-relaxed text-slate-600 md:p-8" data-id="element-165">
            <p data-id="element-166">
              Abrís los tomos.{' '}
              <b className="text-slate-900" data-id="element-167">Los usás en tus entrenamientos.</b>
            </p>
            <p className="mt-3" data-id="element-168">
              Y si en 7 días sentís que no era lo que esperabas, escribís a
              compradigitalyaa@gmail.com y te devolvemos el dinero.
            </p>
            <div className="mt-4 flex flex-wrap gap-4 text-sm font-bold text-slate-900" data-id="element-169">
              {['Sin vueltas', 'Sin formularios eternos', 'Sin preguntas'].map(text => <span key={text} className="flex items-center gap-1.5" data-id="element-170">
                    <CheckCircle2Icon className="h-5 w-5 text-teal" data-id="element-171" />
                    {text}
                  </span>)}
            </div>
          </div>
          <p className="mt-6 text-lg font-bold text-slate-900" data-id="element-172">
            El riesgo no es tuyo. <span className="text-teal" data-id="element-173">Es nuestro.</span>
          </p>
        </div>
      </section>

      <section className="border-y border-sky-100 bg-white px-4 py-16 md:py-24" data-id="element-174">
        <div className="mx-auto max-w-4xl" data-id="element-175">
          <Eyebrow danger data-id="element-176">⚖️ El costo real</Eyebrow>
          <SectionTitle data-id="element-177">
            ¿Cuánto te cuesta <span className="text-red-500" data-id="element-178">NO tenerlo?</span>
          </SectionTitle>
          <div className="mt-10 grid gap-3 sm:grid-cols-2" data-id="element-179">
            {costs.map(([Icon, label, cost]) => {
            const CostIcon = Icon as typeof ClockIcon;
            return <div key={label as string} className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50/50 p-4 shadow-sm" data-id="element-180">
                  <span className="rounded-lg border border-red-100 bg-red-50 p-2" data-id="element-181">
                    <CostIcon className="h-5 w-5 text-red-500" data-id="element-182" />
                  </span>
                  <p data-id="element-183">
                    <b className="block text-sm text-slate-900" data-id="element-184">
                      {label as string}
                    </b>
                    <span className="text-sm font-medium text-slate-500" data-id="element-185">
                      {cost as string}
                    </span>
                  </p>
                </div>;
          })}
          </div>
          <p className="mt-10 rounded-2xl border border-teal/20 bg-white p-6 text-center text-lg font-bold text-slate-700 shadow-sm md:text-2xl" data-id="element-186">
            El próximo entrenamiento puede ser igual.
            <br data-id="element-187" />
            <span className="text-teal" data-id="element-188">
              O puede ser el primero con un sistema real.
            </span>
          </p>
        </div>
      </section>
    </>;
}